import { EngagementType, TriviaRoundStatus } from '@/generated/graphql';

const questions = [
  {
    id: 'q1',
    text: 'What is the capital of Australia?',
    correctChoiceId: 'c-canberra',
    timerMs: null,
    choices: [
      { id: 'c-sydney', text: 'Sydney' },
      { id: 'c-canberra', text: 'Canberra' },
      { id: 'c-melbourne', text: 'Melbourne' },
      { id: 'c-perth', text: 'Perth' },
    ],
  },
  {
    id: 'q2',
    text: 'How many sides does a hexagon have?',
    correctChoiceId: 'c-six',
    timerMs: null,
    choices: [
      { id: 'c-five', text: 'Five' },
      { id: 'c-six', text: 'Six' },
    ],
  },
];

const longQuestion = {
  id: 'qlong',
  text: 'Which of these Australian animals is both a marsupial and famous for carrying joeys in a pouch while hopping across the outback at dusk?',
  correctChoiceId: 'c-kanga',
  timerMs: 30000,
  choices: [
    { id: 'c-kanga', text: 'Kangaroo' },
    { id: 'c-koala', text: 'Koala' },
    { id: 'c-wombat', text: 'Wombat' },
    { id: 'c-emu', text: 'Emu' },
    { id: 'c-plat', text: 'Platypus' },
    { id: 'c-dingo', text: 'Dingo' },
  ],
};

const maxQuestion = {
  id: 'qmax',
  text: 'Q'.repeat(140),
  correctChoiceId: 'm1',
  timerMs: 30000,
  choices: Array.from({ length: 6 }, (_, i) => ({
    id: `m${i + 1}`,
    text: 'C'.repeat(48),
  })),
};

const leaderboard = [
  { userId: 'u1', name: 'Zany Quokka', score: 9, rank: 1, tied: true },
  { userId: 'u2', name: 'Sunny Wombat', score: 9, rank: 1, tied: true },
  { userId: 'u3', name: 'Brave Emu', score: 8, rank: 3, tied: false },
  { userId: 'u4', name: 'Witty Dingo', score: 7, rank: 4, tied: false },
  { userId: 'u5', name: 'Lucky Koala', score: 6, rank: 5, tied: true },
  { userId: 'u6', name: 'Chill Possum', score: 6, rank: 5, tied: true },
];

function stripCorrect(
  qs: Array<{
    id: string;
    text: string;
    timerMs: number | null;
    choices: Array<{ id: string; text: string }>;
    correctChoiceId?: string | null;
  }>,
) {
  return qs.map((q) => ({
    id: q.id,
    text: q.text,
    timerMs: q.timerMs,
    choices: q.choices,
  }));
}

function baseEngagement(
  viewData: Record<string, unknown>,
  viewConfig: Record<string, unknown> = {},
  adminConfig: Record<string, unknown> = {},
) {
  return {
    id: 'preview-trivia',
    eventId: 'preview',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    title: 'Trivia Night',
    description: 'Get ready! First question coming up.',
    qrCodeCta: 'Scan to play!',
    startTime: null,
    endTime: null,
    type: EngagementType.Trivia,
    order: 1,
    config: {
      __typename: 'TriviaAdminConfig',
      instructions: 'Get ready! First question coming up.',
      useQuestionTimer: true,
      defaultTimerMs: 30000,
      questions,
      ...adminConfig,
    },
    data: {
      __typename: 'TriviaAdminData',
      playedThroughIndex: 0,
      revealedQuestionIds: [],
      skipRevealedQuestionIds: [],
      skipped: false,
      ...viewData,
    },
    viewConfig: {
      __typename: 'TriviaViewConfig',
      instructions: 'Get ready! First question coming up.',
      useQuestionTimer: true,
      defaultTimerMs: 30000,
      questions: stripCorrect(questions),
      ...viewConfig,
    },
    viewData: {
      __typename: 'TriviaViewData',
      leaderboard: [],
      tallies: [],
      leaderboardOverflow: 0,
      skipped: false,
      ...viewData,
    },
  };
}

const editorQuestions = [
  questions[0],
  questions[1],
  {
    id: 'q3',
    text: 'Which planet has the most moons?',
    correctChoiceId: 'c-saturn',
    timerMs: null,
    choices: [
      { id: 'c-jupiter', text: 'Jupiter' },
      { id: 'c-saturn', text: 'Saturn' },
      { id: 'c-uranus', text: 'Uranus' },
      { id: 'c-neptune', text: 'Neptune' },
    ],
  },
  {
    id: 'q4',
    text: 'Who painted the Mona Lisa?',
    correctChoiceId: 'c-vinci',
    timerMs: null,
    choices: [
      { id: 'c-vinci', text: 'Leonardo da Vinci' },
      { id: 'c-vangogh', text: 'Vincent van Gogh' },
      { id: 'c-picasso', text: 'Pablo Picasso' },
      { id: 'c-monet', text: 'Claude Monet' },
    ],
  },
  {
    id: 'q5',
    text: "What's the largest ocean on Earth?",
    correctChoiceId: 'c-pacific',
    timerMs: 45000,
    choices: [
      { id: 'c-atlantic', text: 'Atlantic' },
      { id: 'c-pacific', text: 'Pacific' },
      { id: 'c-indian', text: 'Indian' },
      { id: 'c-arctic', text: 'Arctic' },
    ],
  },
  {
    id: 'q6',
    text: 'Which element has the symbol Fe?',
    correctChoiceId: 'c-iron',
    timerMs: null,
    choices: [
      { id: 'c-fluorine', text: 'Fluorine' },
      { id: 'c-iron', text: 'Iron' },
    ],
  },
  {
    id: 'q7',
    text: 'In what year did Steve Jobs walk on stage in San Francisco and show the whole world the very first Apple iPhone?',
    correctChoiceId: 'c-2007',
    timerMs: null,
    choices: [
      { id: 'c-2005', text: '2005' },
      { id: 'c-2006', text: '2006' },
      { id: 'c-2007', text: '2007, at the Macworld keynote in January' },
      { id: 'c-2008', text: '2008' },
    ],
  },
  {
    id: 'q8',
    text: "What's the fastest land animal?",
    correctChoiceId: 'c-cheetah',
    timerMs: 20000,
    choices: [
      { id: 'c-cheetah', text: 'Cheetah' },
      { id: 'c-lion', text: 'Lion' },
      { id: 'c-horse', text: 'Horse' },
      { id: 'c-hare', text: 'Hare' },
    ],
  },
  {
    id: 'q9',
    text: 'How many players are on a soccer team?',
    correctChoiceId: 'c-eleven',
    timerMs: null,
    choices: [
      { id: 'c-nine', text: '9' },
      { id: 'c-ten', text: '10' },
      { id: 'c-eleven', text: '11' },
    ],
  },
  {
    id: 'q10',
    text: 'Which country gave the US the Statue of Liberty?',
    correctChoiceId: 'c-france',
    timerMs: null,
    choices: [
      { id: 'c-france', text: 'France' },
      { id: 'c-uk', text: 'United Kingdom' },
      { id: 'c-spain', text: 'Spain' },
      { id: 'c-italy', text: 'Italy' },
    ],
  },
];

const padQuestions = (count = 10) => {
  const extra = Array.from(
    { length: Math.max(0, count - questions.length) },
    (_, i) => ({
      id: `qx${i}`,
      text: `Warm-up question ${i + 3}`,
      timerMs: null,
      choices: [
        { id: `qx${i}a`, text: 'Alpha' },
        { id: `qx${i}b`, text: 'Beta' },
      ],
    }),
  );
  return [...questions, ...extra];
};

const endsAt = new Date(Date.now() + 18_000).toISOString();

const soloLeaderboard = [
  { userId: 'u1', name: 'Zany Quokka', score: 9, rank: 1, tied: false },
  { userId: 'u2', name: 'Jolly Otter', score: 8, rank: 2, tied: true },
  { userId: 'u3', name: 'Bouncy Capybara', score: 8, rank: 2, tied: true },
  { userId: 'u4', name: 'Test Wombat', score: 7, rank: 4, tied: false },
  { userId: 'u5', name: 'Sneaky Panda', score: 6, rank: 5, tied: true },
  { userId: 'u6', name: 'Lucky Llama', score: 6, rank: 5, tied: true },
];

const tenQs = padQuestions();
const tenQsView = stripCorrect(tenQs);

export const TRIVIA_PREVIEW_FRAMES = {
  'config-empty': (() => {
    const engagement = baseEngagement({
      status: TriviaRoundStatus.Idle,
      questionIndex: 0,
      playerCount: 0,
    });
    return {
      ...engagement,
      config: { ...engagement.config, questions: [] },
      viewConfig: { ...engagement.viewConfig, questions: [] },
    };
  })(),
  'config-populated': (() => {
    const engagement = baseEngagement(
      {
        status: TriviaRoundStatus.Idle,
        questionIndex: 0,
        playerCount: 0,
      },
      { questions: stripCorrect(editorQuestions) },
      {
        questions: editorQuestions,
        instructions: '10 questions. +1 point for every right answer.',
      },
    );
    return {
      ...engagement,
      title: 'Trivia Night',
      description: '10 questions. +1 point for every right answer.',
    };
  })(),
  'config-blank-error': (() => {
    const blank = {
      id: 'q-blank',
      text: '',
      correctChoiceId: null,
      timerMs: null,
      choices: [
        { id: 'b1', text: '' },
        { id: 'b2', text: '' },
      ],
    };
    const engagement = baseEngagement(
      {
        status: TriviaRoundStatus.Idle,
        questionIndex: 0,
        playerCount: 0,
      },
      { questions: stripCorrect([blank]) },
      { questions: [blank] },
    );
    return engagement;
  })(),
  'config-round-live': (() => {
    const engagement = baseEngagement(
      {
        status: TriviaRoundStatus.QuestionOpen,
        questionIndex: 0,
        playedThroughIndex: 0,
        playerCount: 24,
      },
      { questions: stripCorrect(editorQuestions) },
      { questions: editorQuestions },
    );
    return engagement;
  })(),
  'config-round-complete': (() => {
    const engagement = baseEngagement(
      {
        status: TriviaRoundStatus.RoundComplete,
        questionIndex: editorQuestions.length - 1,
        playedThroughIndex: editorQuestions.length - 1,
        playerCount: 24,
      },
      { questions: stripCorrect(editorQuestions) },
      { questions: editorQuestions },
    );
    return engagement;
  })(),
  'config-custom-timer': (() => {
    const customQ = {
      ...questions[0],
      timerMs: 90_000,
    };
    const engagement = baseEngagement(
      {
        status: TriviaRoundStatus.Idle,
        questionIndex: 0,
        playerCount: 0,
      },
      { questions: stripCorrect([customQ]) },
      { questions: [customQ] },
    );
    return engagement;
  })(),
  'stage-idle': baseEngagement(
    {
      status: TriviaRoundStatus.Idle,
      questionIndex: 0,
      playerCount: 24,
      answeredCount: 0,
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'stage-open': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
      playerCount: 52,
      answeredCount: 37,
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'stage-open-long': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
      playerCount: 24,
      answeredCount: 10,
    },
    { questions: stripCorrect([longQuestion]) },
  ),
  'stage-open-max': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
      playerCount: 24,
      answeredCount: 10,
    },
    { questions: stripCorrect([maxQuestion]) },
  ),
  'stage-skipped': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionClosed,
      questionIndex: 1,
      playerCount: 4,
      answeredCount: 3,
      skipped: true,
      revealedQuestionIds: ['q1', 'q2'],
      skipRevealedQuestionIds: ['q2'],
      correctChoiceId: 'c-six',
      correctCount: 2,
      tallies: [
        { choiceId: 'c-five', count: 1 },
        { choiceId: 'c-six', count: 2 },
      ],
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'stage-closed': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionClosed,
      questionIndex: 0,
      playerCount: 52,
      answeredCount: 48,
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'stage-revealed': baseEngagement(
    {
      status: TriviaRoundStatus.Revealed,
      questionIndex: 0,
      playerCount: 52,
      answeredCount: 48,
      correctChoiceId: 'c-canberra',
      correctCount: 23,
      tallies: [
        { choiceId: 'c-sydney', count: 12 },
        { choiceId: 'c-canberra', count: 23 },
        { choiceId: 'c-melbourne', count: 8 },
        { choiceId: 'c-perth', count: 5 },
      ],
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'stage-revealed-long': baseEngagement(
    {
      status: TriviaRoundStatus.Revealed,
      questionIndex: 0,
      correctChoiceId: 'c-kanga',
      answeredCount: 40,
      correctCount: 22,
      playerCount: 40,
      tallies: longQuestion.choices.map((c, i) => ({
        choiceId: c.id,
        count: i === 0 ? 22 : 3,
      })),
    },
    { questions: stripCorrect([longQuestion]) },
  ),
  'stage-revealed-max': baseEngagement(
    {
      status: TriviaRoundStatus.Revealed,
      questionIndex: 0,
      correctChoiceId: 'm1',
      answeredCount: 40,
      correctCount: 10,
      playerCount: 40,
      tallies: maxQuestion.choices.map((c) => ({ choiceId: c.id, count: 6 })),
    },
    { questions: stripCorrect([maxQuestion]) },
  ),
  'stage-complete': baseEngagement(
    {
      status: TriviaRoundStatus.RoundComplete,
      questionIndex: 9,
      playerCount: 52,
      answeredCount: 52,
      leaderboard: soloLeaderboard,
    },
    { questions: tenQsView },
  ),
  'stage-complete-co': baseEngagement(
    {
      status: TriviaRoundStatus.RoundComplete,
      questionIndex: 9,
      playerCount: 52,
      leaderboard,
    },
    { questions: stripCorrect(padQuestions()) },
  ),
  'fan-idle': baseEngagement(
    {
      status: TriviaRoundStatus.Idle,
      questionIndex: 0,
      playerCount: 24,
    },
    { questions: tenQsView },
  ),
  'fan-choose': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
      playerCount: 52,
      answeredCount: 37,
    },
    { questions: tenQsView },
  ),
  'fan-choose-long': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
    },
    { questions: stripCorrect([longQuestion]) },
  ),
  'fan-choose-max': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
    },
    { questions: stripCorrect([maxQuestion]) },
  ),
  'fan-locked': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
    },
    { questions: tenQsView },
  ),
  'fan-missed': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionClosed,
      questionIndex: 0,
    },
    { questions: tenQsView },
  ),
  'fan-correct': baseEngagement(
    {
      status: TriviaRoundStatus.Revealed,
      questionIndex: 0,
      correctChoiceId: 'c-canberra',
      correctCount: 23,
      answeredCount: 48,
      leaderboard: [
        { userId: 'me', name: 'You', score: 1, rank: 1, tied: false },
      ],
    },
    { questions: tenQsView },
  ),
  'fan-wrong': baseEngagement(
    {
      status: TriviaRoundStatus.Revealed,
      questionIndex: 0,
      correctChoiceId: 'c-canberra',
    },
    { questions: tenQsView },
  ),
  'fan-complete': baseEngagement(
    {
      status: TriviaRoundStatus.RoundComplete,
      playerCount: 52,
      leaderboard: [
        { userId: 'u1', name: 'Zany Quokka', score: 9, rank: 1, tied: true },
        { userId: 'u2', name: 'Sunny Wombat', score: 9, rank: 1, tied: true },
        { userId: 'me', name: 'You', score: 7, rank: 4, tied: false },
        ...leaderboard.slice(2),
      ],
    },
    { questions: stripCorrect(padQuestions()) },
  ),
  'fan-complete-co': baseEngagement(
    {
      status: TriviaRoundStatus.RoundComplete,
      playerCount: 52,
      leaderboard: [
        { userId: 'me', name: 'You', score: 9, rank: 1, tied: true },
        ...leaderboard,
      ],
    },
    { questions: stripCorrect(padQuestions()) },
  ),
  'fan-complete-empty': baseEngagement(
    {
      status: TriviaRoundStatus.RoundComplete,
      playerCount: 52,
      leaderboard,
    },
    { questions: stripCorrect(padQuestions()) },
  ),
  'control-idle': baseEngagement(
    {
      status: TriviaRoundStatus.Idle,
      questionIndex: 0,
      playerCount: 24,
      answeredCount: 0,
      correctChoiceId: 'c-canberra',
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'control-open': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionOpen,
      questionIndex: 0,
      questionEndsAt: endsAt,
      playerCount: 52,
      answeredCount: 37,
      correctChoiceId: 'c-canberra',
      tallies: [
        { choiceId: 'c-sydney', count: 8 },
        { choiceId: 'c-canberra', count: 18 },
        { choiceId: 'c-melbourne', count: 7 },
        { choiceId: 'c-perth', count: 4 },
      ],
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'control-closed': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionClosed,
      questionIndex: 0,
      playerCount: 52,
      answeredCount: 48,
      correctChoiceId: 'c-canberra',
      tallies: [
        { choiceId: 'c-sydney', count: 12 },
        { choiceId: 'c-canberra', count: 23 },
        { choiceId: 'c-melbourne', count: 8 },
        { choiceId: 'c-perth', count: 5 },
      ],
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'control-skipped': baseEngagement(
    {
      status: TriviaRoundStatus.QuestionClosed,
      questionIndex: 1,
      playerCount: 4,
      answeredCount: 3,
      skipped: true,
      reviewing: true,
      revealedQuestionIds: ['q1', 'q2'],
      skipRevealedQuestionIds: ['q2'],
      correctChoiceId: 'c-six',
      correctCount: 2,
      tallies: [
        { choiceId: 'c-five', count: 1 },
        { choiceId: 'c-six', count: 2 },
      ],
      leaderboard: leaderboard.slice(0, 4),
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'control-revealed': baseEngagement(
    {
      status: TriviaRoundStatus.Revealed,
      questionIndex: 0,
      playerCount: 52,
      answeredCount: 48,
      correctCount: 23,
      correctChoiceId: 'c-canberra',
      revealedQuestionIds: ['q1'],
      tallies: [
        { choiceId: 'c-sydney', count: 12 },
        { choiceId: 'c-canberra', count: 23 },
        { choiceId: 'c-melbourne', count: 8 },
        { choiceId: 'c-perth', count: 5 },
      ],
      leaderboard: leaderboard.slice(0, 5),
    },
    { questions: tenQsView },
    { questions: tenQs },
  ),
  'control-complete': baseEngagement(
    {
      status: TriviaRoundStatus.RoundComplete,
      questionIndex: 9,
      playerCount: 52,
      leaderboard,
    },
    { questions: stripCorrect(padQuestions()) },
    { questions: padQuestions() },
  ),
} as const;

export type TriviaPreviewFrame = keyof typeof TRIVIA_PREVIEW_FRAMES;
